import { Link } from 'react-router-dom';
import { BookOpen, CalendarRange } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

interface TermsGuideOverviewProps {
  className?: string;
}

/** Terms page overview — shared by the page help popover and the empty state. */
export function TermsGuideOverview({ className }: TermsGuideOverviewProps) {
  return (
    <div className={cn('space-y-4', className)}>
      <p className="text-sm text-muted-foreground">
        Terms are date ranges that organise your recurring season—group lessons, clinics, and
        term-bound private 1-on-1 sessions run inside a term. They are ideal for charging students
        the full cost of lessons upfront. Families enrol for the entire term on your public academy
        page.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-muted/40 p-3 text-left">
          <div className="mb-1.5 flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-primary" aria-hidden />
            <h4 className="text-sm font-medium text-foreground">Seasonal blocks</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            Spring, summer, or school-term periods with start and end dates. Set an optional
            registration deadline to close enrolments before the term begins.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-muted/40 p-3 text-left">
          <div className="mb-1.5 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" aria-hidden />
            <h4 className="text-sm font-medium text-foreground">Group lessons & programs</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            Link group lessons and term-bound private sessions to a term so bookings respect the
            season. Copy lessons from a previous term when rolling into the next one.
          </p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Use{' '}
        <Link
          to="/admin/camps"
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          Camps
        </Link>{' '}
        to create sessions that run outside terms and charge students on a weekly or daily basis—or
        upfront for the full camp. Standalone private packages do not use terms either; add them on
        the calendar or from Lessons.
      </p>
    </div>
  );
}

interface TermsGuideHelpContentProps {
  onStartTour: () => void;
}

/** Rich help content for the Terms page — shown in the top-bar Page help popover. */
export function TermsGuideHelpContent({ onStartTour }: TermsGuideHelpContentProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-1 text-sm font-semibold text-foreground">Academic terms</h3>
        <TermsGuideOverview />
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onStartTour}>
        Take a quick tour
      </Button>
    </div>
  );
}
